import Link from "next/link";
import Image from "next/image";
import { storyblokEditable } from "@storyblok/react/rsc";
import { renderRichText } from "@storyblok/react";
import { format, toDate } from "date-fns-tz";
import { de } from "date-fns/locale";
import { isAfter, isBefore, startOfDay } from "date-fns";

import { H1, H3, RichText } from "@/components/typography";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { Button } from "@/components/layout/Button";
import type { EventStoryblok, CampsiteStoryblok } from "@/component-types-sb";
import imageLoader from "@/lib/utils/imageLoader";

const EVENT_TIME_ZONE = "Europe/Berlin";

type EventProps = {
  blok: EventStoryblok;
  searchParams?: { [key: string]: string | string[] | undefined };
};

const getDateParam = (searchParams?: EventProps["searchParams"]) => {
  const date = searchParams?.date;

  if (Array.isArray(date)) {
    return date[0];
  }

  return date;
};

const parseEventDate = (date?: string) => {
  if (!date) {
    return null;
  }

  return toDate(date, { timeZone: "GMT" });
};

const formatLongDate = (date: Date) =>
  format(date, "PPPP", {
    locale: de,
    timeZone: EVENT_TIME_ZONE,
  });

const formatRangeDate = (date: Date) =>
  format(date, "d. MMMM yyyy", {
    locale: de,
    timeZone: EVENT_TIME_ZONE,
  });

const repeatLabels: Record<NonNullable<EventStoryblok["repeat"]>, string> = {
  "": "",
  daily: "Täglich",
  weekly: "Wöchentlich",
  monthly: "Monatlich",
};

const getEventDateDisplay = (
  blok: EventStoryblok,
  searchParams?: EventProps["searchParams"]
) => {
  const startDate = parseEventDate(blok.date);

  if (!startDate) {
    return null;
  }

  const untilDate = parseEventDate(blok.until);
  const endDate =
    untilDate && isAfter(untilDate, startDate) ? untilDate : undefined;
  const dateParam = getDateParam(searchParams);
  const selectedDate =
    dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)
      ? toDate(`${dateParam} 00:00`, { timeZone: EVENT_TIME_ZONE })
      : null;
  const repeatLabel = repeatLabels[blok.repeat || ""];
  const rangeLabel = endDate
    ? `${repeatLabel ? `${repeatLabel} vom` : "Vom"} ${formatRangeDate(
        startDate
      )} bis ${formatRangeDate(endDate)}`
    : null;

  if (selectedDate && endDate) {
    const selectedDay = startOfDay(selectedDate);

    if (
      !isBefore(selectedDay, startOfDay(startDate)) &&
      !isAfter(selectedDay, startOfDay(endDate))
    ) {
      return {
        title: formatLongDate(selectedDate),
        subtitle: rangeLabel,
        dateTime: dateParam,
      };
    }
  }

  return {
    title: rangeLabel || formatLongDate(startDate),
    dateTime: blok.date,
  };
};

const Event = ({ blok, searchParams }: EventProps) => {
  const dateDisplay = getEventDateDisplay(blok, searchParams);

  return (
    <article className="w-full pb-8" {...storyblokEditable(blok)}>
      <section className="gap-16 items-center py-8 px-4 mx-auto max-w-screen-xl lg:grid lg:grid-cols-2 lg:py-16 lg:px-6">
        <div>
          <header>
            <Breadcrumb
              items={[
                { href: "/veranstaltungen", title: "Veranstaltungen" },
                { title: blok.title },
              ]}
            />
            <H1 colorScheme="primary">{blok.title}</H1>
            {dateDisplay && (
              <div>
                <H3 as="h2">
                  <time dateTime={dateDisplay.dateTime}>
                    {dateDisplay.title}
                  </time>
                </H3>
                {dateDisplay.subtitle && (
                  <p className="mt-2 text-lg text-gray-600">
                    {dateDisplay.subtitle}
                  </p>
                )}
              </div>
            )}
          </header>

          <RichText html={renderRichText(blok.content)} />

          <div className="flex gap-2">
            {blok.campsite && (
              <Link
                href={[
                  "/",
                  (blok.campsite as unknown as CampsiteStoryblok).full_slug,
                ].join("")}
              >
                <Button colorScheme="primary" className="my-4">
                  {(blok.campsite as unknown as CampsiteStoryblok).name}
                </Button>
              </Link>
            )}

            {blok.link?.cached_url && (
              <Link href={["/", blok.link?.cached_url].join("")}>
                <Button arrow colorScheme="secondary" className="my-4">
                  Mehr Infos
                </Button>
              </Link>
            )}
          </div>

          {/* {nextSchedules.length > 0 && (
            <>
              <div className="relative inline-flex items-center justify-center w-full">
                <hr className="w-64 h-px my-8 bg-gray-200 border-0" />
                <span className="absolute px-3 text-sm font-medium text-gray-500 -translate-x-1/2 bg-white left-1/2">
                  Weitere Termine
                </span>
              </div>

              <ul className="grid grid-cols-2 gap-2">
                {nextSchedules.map((date, index) => (
                  <li key={index}>{formatDate(date)}</li>
                ))}
              </ul>
            </>
          )} */}
        </div>

        <div className="grid grid-flow-col gap-2">
          {blok.images?.map((image) => (
            <Image
              key={image.id}
              className="object-cover w-full transition duration-500 aspect-square"
              loader={imageLoader}
              src={image.filename}
              width={300}
              height={300}
              sizes="20vw"
              alt={image.alt || ""}
            />
          ))}
        </div>
      </section>
    </article>
  );
};

export default Event;
